import { handleActions } from 'redux-actions';
import { LOCATION_CHANGE, replace } from 'react-router-redux';
import endpoints from '../../../config/endpoints';
import { getFormValues } from 'redux-form';

import http from '../../shared/http';
import intl from '../../shared/intl';
import checkPermissions from '../../shared/check-permissions';

import qs from 'qs';
import moment from 'moment';
import cloneDeep from 'lodash/cloneDeep';
import range from 'lodash/range';
import find from 'lodash/find';
import orderBy from 'lodash/orderBy';
import values from 'lodash/values';

import createTable from '../components/table/index';
import createForm from '../components/adv-requests-form';
import { default as createCalendar, getDateRangeFromCalendar } from '../../shared/calendar/index';

const calendar = createCalendar({
	namespace: 'advRequestsList',
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment().subtract(1, 'month').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const table = createTable({
	namespace: 'advRequestsList'
});

const advForm = createForm({
	namespace: 'advRequestsList',
	stateNodeName: 'advForm',
	formName: 'advForm'
});
// action types
const FETCH_DATA_SUCCESS = 'adv-requests-list/fetch-data-success';
const FETCH_DATA_ERROR = 'adv-requests-list/fetch-data-error';

const APPLY = 'adv-requests-list/apply';

const GOTO_PAGE_SUCCESS = 'adv-requests-list/goto-page-success';
const TOGGLE_PROGRESS_BAR = 'adv-requests-list/toggle-progress-bar';

const APPLY_QUICK_FILTER = 'adv-requests-list/apply-quick-filter';

const SET_SEARCH_QUERY = 'adv-requests-list/set-search-query';

// permissions
export const allowedPermissions = {
	'view': 'adv_request_list'
};

// initial state
const initialState = {
	data: null,
	table: table.initialState,
	calendar: calendar.initialState,
	pagination: {
		currentPage: 1,
		perPage: 100,
		total: null
	},
	search: '',
	quickFilters: [
		{ name: 'all', placeholder: 'All', active: true },
		{ name: 'unread', placeholder: 'Unread', active: false },
		{ name: 'in_work', placeholder: 'In work', active: false }
	],
	showProgressBar: false,
	advForm: advForm.initialState,
	error: false
};

// actions
export const actions = Object.assign(
	{
		fetchData: props => (dispatch, getState) => {
			const { perPage } = getState().advRequestsList.pagination;

			const externalQuery = (props.location && props.location.query && qs.parse(props.location.query)) || {};
			return request(0, perPage, getState, externalQuery && externalQuery.query)(dispatch);
		},

		apply: () => (dispatch, getState) => {
			const searchForm = getFormValues('advRequestsSearchForm')(getState());
			const search = searchForm && searchForm.search;

			dispatch(actions.toggleProgressBar());
			runActionsWithDelay(() => dispatch({ type: APPLY, payload: search }));
		},

		search: () => (dispatch, getState) => {
			dispatch(actions.setSearchQuery());
			dispatch(request(0, null, getState, {}));
		},

		setSearchQuery: () => (dispatch, getState) => {
			const searchForm = getFormValues('advRequestsSearchForm')(getState());
			const search = searchForm && searchForm.search || '';

			dispatch({
				type: SET_SEARCH_QUERY,
				payload: search
			});
		},

		clear: () => dispatch => {
			dispatch(actions.toggleProgressBar());
			runActionsWithDelay(() => dispatch(replace('/adv-requests')));
		},

		toggleProgressBar: () => ({
			type: TOGGLE_PROGRESS_BAR
		}),

		gotoPage: page => (dispatch, getState) => {
			const { perPage } = getState().advRequestsList.pagination;
			const offset = (page - 1) * perPage;

			dispatch(actions.toggleProgressBar());

			request(offset, perPage, getState)(dispatch).then(action => {
				if (action.type === FETCH_DATA_SUCCESS) {
					dispatch({
						type: GOTO_PAGE_SUCCESS,
						payload: page
					});
				}
			});
		},

		applyQuickFilter: filterName => dispatch => {
			dispatch(actions.toggleProgressBar());
			runActionsWithDelay(() => dispatch({ type: APPLY_QUICK_FILTER, payload: filterName }));
		}
	},
	calendar.actions,
	advForm.actions,
	table.actions
);

const reducers = Object.assign(
	{
		[LOCATION_CHANGE]: (state, action) => {
			return initialState;
		},
		[FETCH_DATA_SUCCESS]: (state, action) => {
			return {
				...state,
				data: action.payload.data.rows,
				pagination: {
					...state.pagination,
					total: action.payload.data.total
				},
				showProgressBar: false,
				error: false
			};
		},
		[APPLY]: (state, action) => ({ ...state, data: null, search: action.payload, showProgressBar: false }),
		[GOTO_PAGE_SUCCESS]: (state, action) => ({
			...state,
			pagination: {
				...state.pagination,
				currentPage: action.payload
			}
		}),
		[APPLY_QUICK_FILTER]: (state, action) => ({
			...state,
			data: null,
			showProgressBar: false,
			quickFilters: state.quickFilters.map(filter => {
				return filter.name === action.payload ? { ...filter, active: true } : { ...filter, active: false };
			})
		}),
		[FETCH_DATA_ERROR]: (state, action) => ({ ...state, error: true, showProgressBar: false }),
		[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: !state.showProgressBar }),
		[SET_SEARCH_QUERY]: (state, action) => ({ ...state, search: action.payload })
	},
	calendar.reducers,
	advForm.reducers,
	table.reducers
);

// reducers
export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	getData: state => state.advRequestsList.data,
	getTable: state => {
		const { table, data } = state.advRequestsList;
		const rows = data ? sortColumns(data, table.sortByColumn) : [];

		let tableData = rows.map(row => ({
			...row
		}));

		tableData = groupRowsByType(tableData);

		return {
			headers: values(table.columns),
			rows: tableData,
			sortedColumn: table.sortByColumn,
			view: table.view
		};
	},
	getSearchData: state => ({ search: state.advRequestsList.search }),
	getQuickFilters: state => state.advRequestsList.quickFilters,
	getCalendar: state => calendar.selectors.getCalendar(state => state.advRequestsList.calendar, state => state)(state),
	getPagination: state => {
		const { perPage, total, currentPage } = state.advRequestsList.pagination;
		return {
			pages: range(Math.ceil(total / perPage)),
			currentPage,
			total
		};
	},
	getShouldShowAdvRequestForm: advForm.selectors.getShouldShowAdvRequestForm,
	getAdvFormEditMode: advForm.selectors.getAdvFormEditMode,
	getModalData: advForm.selectors.getModalData,
	getInitialValues: advForm.selectors.getInitialValues,
	getAdvFormOpenedId: advForm.selectors.getAdvFormOpenedId,
	getProgressBar: state => state.advRequestsList.showProgressBar,
	getIntl: state => intl(state),
	getCan: state => checkPermissions(state, allowedPermissions),
	getError: state => state.advRequestsList.error
};

const request = (offset = 0, limit = 5, getState, externalQuery = {}) => {
	const { calendar, search, quickFilters } = getState().advRequestsList;
	const activeQuickFilter = find(quickFilters, { active: true });

	const query = qs.stringify({
		offset,
		limit,
		search,
		quick_filter: activeQuickFilter && activeQuickFilter.name,
		date_range: getDateRangeFromCalendar(calendar),
		...externalQuery
	});

	return http({
		url: `${endpoints.ADV_REQUESTS.BASE}?${query}`,
		method: 'get',
		actions: [FETCH_DATA_SUCCESS, FETCH_DATA_ERROR]
	});
};

const sortColumns = (rows, sortedColumn) => {
	if (!rows) return null;
	return orderBy(
		rows,
		row => {
			const value = row[sortedColumn.index];
			switch (sortedColumn.index) {
				case 'created_at':
					return moment(value).valueOf();
				default:
					return value;
			}
		},
		sortedColumn.direction
	);
};

const groupRowsByType = rows => {
	const res = [
		{ name: 'Unread', data: [], className: 'unread' },
		{ name: 'in work', data: [], className: 'in-work' }
	];

	rows.forEach(row => {
		if (!row.admin_viewed) {
			res[0]['data'].push(row);
		} else {
			res[1]['data'].push(row);
		}
	});

	return res;
};
/**
 * Wait when to end all action before view transit to 'uninitialized' state
 */
const runActionsWithDelay = actionFn => setTimeout(actionFn);



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/advertisers-requests/list/index.js